import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { usePagination } from '@/hooks/usePagination';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, Trash2, ExternalLink, HardDrive, RefreshCw, Copy, Play, Info } from 'lucide-react';
import {
  DEFAULT_MEDIA_FILTERS, MAX_DELETE_BATCH, MEDIA_ACCEPT, filterServerMedia,
  formatMediaSize, mediaThumbnail, usageLabel, readMediaStream, mergeMediaAssets,
} from '@/utils/serverMedia';
import type { MediaDeleteResult, MediaFilters, MediaInventory, ServerMedia } from '@/utils/serverMedia';

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm';
const dateLabel = (value: string) => new Date(value).toLocaleString();
const errorMessage = (error: unknown, fallback: string) => (
  isAxiosError<{ message?: string }>(error) ? error.response?.data?.message || fallback : fallback
);

function MediaThumbnail({ asset }: { asset: ServerMedia }) {
  const [failed, setFailed] = useState(false);
  const thumbnail = mediaThumbnail(asset);
  return (
    <span className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded bg-secondary">
      {!failed && thumbnail
        ? <img src={thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" onError={() => setFailed(true)} />
        : <HardDrive className="h-5 w-5 text-muted-foreground" />}
      {asset.resource_type === 'video' && <Play className="absolute bottom-0 right-0 h-4 w-4 rounded bg-background text-primary" />}
    </span>
  );
}

export default function ServerImageManager() {
  const [inventory, setInventory] = useState<MediaInventory | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [inventoryComplete, setInventoryComplete] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [filters, setFilters] = useState<MediaFilters>(DEFAULT_MEDIA_FILTERS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deleteTargets, setDeleteTargets] = useState<ServerMedia[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const [deleteResults, setDeleteResults] = useState<MediaDeleteResult[]>([]);
  const uploadBusy = useRef(false);
  const deleteBusy = useRef(false);
  const requestVersion = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const protectedIds = useRef<Set<string>>(new Set());

  const fetchInventory = useCallback(async () => {
    if (uploadBusy.current || deleteBusy.current) return;
    const version = ++requestVersion.current;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    const { signal } = controller;
    const seen = new Set<string>();
    protectedIds.current.clear();
    setRefreshing(true);
    setInventoryComplete(false);
    setLoadError('');
    setInventory((previous) => ({
      assets: (previous?.assets || []).map((asset) => ({
        ...asset, usageStatus: 'unknown', usageCount: null, references: [], domains: [], shared: false,
      })),
      domains: previous?.domains || [], usageError: null, checkedAt: null,
    }));
    try {
      const { data } = await axios.get<ReadableStream<Uint8Array>>('/api/server/media/stream', {
        signal, adapter: 'fetch', responseType: 'stream',
      });
      await readMediaStream(data, (event) => {
        if (signal.aborted || version !== requestVersion.current) return;
        if (event.type === 'assets' || event.type === 'usage') {
          if (event.type === 'assets') event.assets.forEach((asset) => seen.add(asset.id));
          setInventory((previous) => previous ? {
            ...previous,
            assets: mergeMediaAssets(previous.assets, event.assets, protectedIds.current),
            ...(event.type === 'usage' ? { domains: event.domains, checkedAt: event.checkedAt } : {}),
          } : previous);
        } else if (event.type === 'warning') {
          setInventory((previous) => previous ? { ...previous, usageError: event.message } : previous);
        } else if (event.type === 'error') {
          setLoadError(event.message);
          toast.error(event.message);
        } else if (event.type === 'done') {
          setInventoryComplete(event.complete);
          if (event.complete) {
            setInventory((previous) => previous ? {
              ...previous, assets: previous.assets.filter((asset) => seen.has(asset.id) || protectedIds.current.has(asset.id)),
            } : previous);
            setSelected((previous) => new Set([...previous].filter((id) => seen.has(id) || protectedIds.current.has(id))));
          }
        }
      });
    } catch (error) {
      if (signal.aborted || version !== requestVersion.current) return;
      setInventoryComplete(false);
      const message = errorMessage(error, 'Media loading was interrupted. Loaded results are incomplete and have been kept. Refresh to retry.');
      setLoadError(message);
      toast.error(message);
    } finally {
      if (!signal.aborted && version === requestVersion.current) {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchInventory();
    return () => requestController.current?.abort();
  }, [fetchInventory]);

  useEffect(() => {
    if (!file) {
      setFilePreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const assets = inventory?.assets;
  const details = assets?.find((asset) => asset.id === detailsId) || null;
  const filtered = useMemo(
    () => filterServerMedia(assets || [], filters, inventory?.domains || []),
    [assets, filters, inventory?.domains],
  );
  const { paginate, paginationProps } = usePagination(filtered.length, {
    defaultPerPage: 25,
    resetDeps: [filters],
  });
  const visible = paginate(filtered);
  const eligibleVisible = visible.filter((asset) => asset.usageStatus === 'unused' && !pendingDeleteIds.has(asset.id));
  const selectedAssets = (assets || []).filter((asset) => selected.has(asset.id));
  const selectableAssets = selectedAssets.filter((asset) => asset.usageStatus === 'unused' && !pendingDeleteIds.has(asset.id));
  const selectionBytes = selectedAssets.reduce((sum, asset) => sum + asset.bytes, 0);
  const allVisibleSelected = eligibleVisible.length > 0 && eligibleVisible.every((asset) => selected.has(asset.id));
  const disabled = uploading;

  const updateFilter = (key: keyof MediaFilters, value: string) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };
  const resetFilters = () => {
    setFilters(DEFAULT_MEDIA_FILTERS);
  };
  const options = (key: 'account' | 'folder' | 'format') => [...new Set((assets || []).map((asset) => asset[key]))].filter(Boolean).sort();
  const chooseFile = (next?: File) => {
    setFile(null);
    if (!next) return;
    if (!MEDIA_ACCEPT.includes(next.type)) {
      toast.error('Choose JPEG, PNG, GIF, WebP, AVIF, MP4, WebM or MOV.');
      return;
    }
    if (next.size > 50 * 1024 * 1024) {
      toast.error('Server media uploads must be 50 MB or smaller.');
      return;
    }
    setFile(next);
    setProgress(0);
  };
  const handleUpload = async () => {
    if (!file || uploadBusy.current) return;
    uploadBusy.current = true;
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post<ServerMedia>('/api/server/upload', form, {
        onUploadProgress: (event) => setProgress(event.total ? Math.round(event.loaded / event.total * 100) : 0),
      });
      protectedIds.current.add(data.id);
      setInventory((previous) => previous ? {
        ...previous, assets: [data, ...previous.assets.filter((asset) => asset.id !== data.id)],
      } : previous);
      toast.success('Media uploaded. Active filters may hide the new asset.');
      setUploadOpen(false);
      setFile(null);
    } catch (error) {
      toast.error(errorMessage(error, 'Media upload failed. Please retry.'));
    } finally {
      uploadBusy.current = false;
      setUploading(false);
    }
  };
  const beginDelete = (targets: ServerMedia[]) => {
    if (deleteBusy.current) return;
    setDeleteTargets(targets);
    setAcknowledged(false);
  };
  const handleDelete = async () => {
    if (deleteBusy.current || !acknowledged || !deleteTargets.length) return;
    const targets = deleteTargets;
    targets.forEach((asset) => protectedIds.current.add(asset.id));
    deleteBusy.current = true;
    setPendingDeleteIds(new Set(targets.map((asset) => asset.id)));
    setDeleteTargets([]);
    setDeleting(true);
    setDeleteResults([]);
    const notification = toast.loading(`Deleting ${targets.length} asset(s). You can keep browsing or upload media.`);
    try {
      const { data } = await axios.post<{ results: MediaDeleteResult[] }>('/api/server/media/delete', {
        assets: targets.map(({ account, resource_type, public_id }) => ({ account, resource_type, public_id })),
        confirmUnreferenced: true,
      });
      const removed = new Set(data.results.filter((result) => ['deleted', 'missing'].includes(result.status)).map((result) => result.id));
      const changes = new Map(data.results.filter((result) => result.asset).map((result) => [result.id, result.asset]));
      const failed = new Set(data.results.filter((result) => result.status === 'failed').map((result) => result.id));
      setInventory((previous) => previous ? {
        ...previous,
        assets: previous.assets.filter((asset) => !removed.has(asset.id)).map((asset) => (
          changes.has(asset.id) ? { ...asset, ...changes.get(asset.id) }
            : failed.has(asset.id) ? { ...asset, usageStatus: 'unknown' } : asset
        )),
      } : previous);
      setSelected((previous) => new Set([...previous].filter((id) => !removed.has(id))));
      setDeleteResults(data.results);
      const failures = data.results.length - removed.size;
      if (failures) toast.error(`${removed.size} removed; ${failures} blocked or failed. See deletion results.`, { id: notification });
      else toast.success(`${removed.size} asset(s) removed from inventory.`, { id: notification });
    } catch (error) {
      const uncertain = new Set(targets.map((asset) => asset.id));
      setInventory((previous) => previous ? {
        ...previous, assets: previous.assets.map((asset) => uncertain.has(asset.id) ? { ...asset, usageStatus: 'unknown' } : asset),
      } : previous);
      toast.error(errorMessage(error, 'Deletion could not be confirmed. Refresh before retrying; some assets may have been removed.'), { id: notification });
    } finally {
      deleteBusy.current = false;
      setPendingDeleteIds(new Set());
      setDeleting(false);
    }
  };
  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied');
    } catch {
      toast.error('Could not copy the URL. Open asset details to copy it manually.');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Server Media</h1>
          <p className="text-sm text-muted-foreground">Images and uploaded videos across all domains and configured storage accounts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={disabled || deleting} onClick={() => void fetchInventory()} loading={refreshing} loadingText="Loading inventory">
            <RefreshCw /> Refresh
          </Button>
          <Button disabled={disabled || !inventory} onClick={() => setUploadOpen(true)}><Upload /> Upload media</Button>
        </div>
      </div>

      {loadError && <div role="alert" className="rounded border border-danger p-3 text-sm text-danger">{loadError}</div>}
      {inventory?.usageError && <div role="alert" className="rounded border border-warning p-3 text-sm">{inventory.usageError}</div>}
      {refreshing && <div role="status" className="rounded border border-border bg-secondary p-3 text-sm">
        {inventory?.assets.length
          ? `${inventory.assets.length} assets loaded. Loading remaining storage pages and checking CMS references in the background.`
          : 'Fetching the first assets from storage. You can use the page while loading continues.'}
        {' '}Filters apply to loaded results until loading finishes. Unverified assets cannot be deleted.
      </div>}
      {deleting && <div role="status" className="rounded border border-border bg-secondary p-3 text-sm">
        Deleting {pendingDeleteIds.size} asset(s). You can keep browsing, filtering and uploading.
        Affected rows are marked below. Refresh and another delete batch are available when this finishes.
      </div>}
      {inventory && (
        <>
          <div className="flex flex-wrap gap-4 rounded-lg border border-border bg-card p-4 text-sm">
            <span><strong>{inventory.assets.length}</strong> {inventoryComplete ? 'assets' : 'assets loaded (partial inventory)'}</span>
            <span><strong>{formatMediaSize(inventory.assets.reduce((sum, asset) => sum + asset.bytes, 0))}</strong> original storage</span>
            <span><strong>{inventory.assets.filter((asset) => asset.usageStatus === 'unused').length}</strong> without CMS references</span>
            <span className="text-muted-foreground">{inventory.checkedAt ? `Usage checked ${dateLabel(inventory.checkedAt)}` : 'Usage not verified'}</span>
          </div>
          <div className="grid gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-2 xl:grid-cols-4">
            <label className="space-y-1 text-xs">Search ID or URL
              <Input value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Search media..." />
            </label>
            <label className="space-y-1 text-xs">Domain / shared assets
              <Input list="media-domains" value={filters.domain} onChange={(event) => updateFilter('domain', event.target.value)} placeholder="All domains - type slug or hostname" />
              <datalist id="media-domains">
                <option value="@shared">Shared/global assets</option>
                <option value="@unassigned">Unassigned - no CMS references</option>
                <option value="@unknown">Unknown usage</option>
                {inventory.domains.map((domain) => <option key={domain.slug} value={domain.slug}>{domain.customDomain || domain.url}</option>)}
              </datalist>
            </label>
            <label className="space-y-1 text-xs">Media type
              <select aria-label="Media type" className={selectClass} value={filters.type} onChange={(event) => updateFilter('type', event.target.value)}>
                <option value="">All media</option><option value="image">Images</option><option value="video">Videos</option>
              </select>
            </label>
            <label className="space-y-1 text-xs">CMS usage
              <select aria-label="CMS usage" className={selectClass} value={filters.usage} onChange={(event) => updateFilter('usage', event.target.value)}>
                <option value="">All usage states</option><option value="inUse">In use</option>
                <option value="unused">No CMS references found</option><option value="unknown">Unknown</option>
              </select>
            </label>
            {(['format', 'account', 'folder'] as const).map((key) => (
              <label key={key} className="space-y-1 text-xs capitalize">{key}
                <select aria-label={key} className={selectClass} value={filters[key]} onChange={(event) => updateFilter(key, event.target.value)}>
                  <option value="">All {key}s</option>
                  {key === 'folder' && <option value="@root">(root)</option>}
                  {options(key).map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>
            ))}
            <label className="space-y-1 text-xs">Sort
              <select aria-label="Sort" className={selectClass} value={filters.sort} onChange={(event) => updateFilter('sort', event.target.value)}>
                <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
                <option value="largest">Largest first</option><option value="smallest">Smallest first</option><option value="name">Name</option>
              </select>
            </label>
            <label className="space-y-1 text-xs">Minimum size (MB)
              <Input type="number" min="0" step="any" value={filters.minSize} onChange={(event) => updateFilter('minSize', event.target.value)} />
            </label>
            <label className="space-y-1 text-xs">Maximum size (MB)
              <Input type="number" min="0" step="any" value={filters.maxSize} onChange={(event) => updateFilter('maxSize', event.target.value)} />
            </label>
            <label className="space-y-1 text-xs">Uploaded from (UTC)
              <Input type="date" value={filters.after} onChange={(event) => updateFilter('after', event.target.value)} />
            </label>
            <label className="space-y-1 text-xs">Uploaded through (UTC)
              <Input type="date" value={filters.before} onChange={(event) => updateFilter('before', event.target.value)} />
            </label>
            <div className="flex items-center gap-3 sm:col-span-2 xl:col-span-4">
              <Button variant="outline" size="sm" onClick={resetFilters}>Clear filters</Button>
              <span className="text-xs text-muted-foreground">{filtered.length} matching of {inventory.assets.length} loaded. {inventoryComplete ? 'Filters cover the complete inventory.' : 'Results are partial; filters update as storage and usage checks arrive.'}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Domain filters use saved references, not upload ownership. Shared assets may appear under multiple domains.
            No CMS references does not mean unused externally. Newly uploaded files may still be awaiting attachment.
          </p>
          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded border border-border bg-secondary p-3" role="status">
              <span className="text-sm">{selectedAssets.length} selected ({formatMediaSize(selectionBytes)}), including hidden selections</span>
              <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>Clear selection</Button>
              <Button variant="destructive" size="sm" disabled={disabled || deleting || !selectableAssets.length || selectableAssets.length > MAX_DELETE_BATCH}
                onClick={() => beginDelete(selectableAssets)}>
                <Trash2 /> Delete {selectableAssets.length} eligible
              </Button>
              <span className="text-xs text-muted-foreground">Maximum {MAX_DELETE_BATCH} per batch; referenced/unknown assets cannot be deleted.</span>
            </div>
          )}
          {deleteResults.length > 0 && (
            <section className="space-y-2 rounded border border-border p-3" aria-label="Deletion results" aria-live="polite">
              <div className="flex items-center justify-between"><h2 className="text-sm font-semibold">Deletion results</h2>
                <Button variant="ghost" size="sm" onClick={() => setDeleteResults([])}>Dismiss</Button>
              </div>
              {deleteResults.map((result) => <p key={result.id} className="break-all text-xs">
                {result.id}: <strong>{result.status}</strong>{result.message ? ` - ${result.message}` : ''}
              </p>)}
            </section>
          )}
          {filtered.length === 0 ? (
            <div className="empty-state">
              <HardDrive className="mx-auto h-9 w-9 text-muted-foreground" />
              <h2 className="empty-state-title">{refreshing ? 'Waiting for matching media...' : 'No matching media'}</h2>
              <p className="empty-state-description">{refreshing ? 'Storage pages and usage checks are still arriving. You can change filters or upload while waiting.' : 'Change or clear the filters, or upload new media.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead><input type="checkbox" aria-label="Select eligible assets on current page" checked={allVisibleSelected} disabled={disabled || !eligibleVisible.length}
                    onChange={(event) => setSelected((previous) => {
                      const next = new Set(previous);
                      eligibleVisible.forEach((asset) => event.target.checked ? next.add(asset.id) : next.delete(asset.id));
                      return next;
                    })} /></TableHead>
                  <TableHead>Preview</TableHead><TableHead>Asset</TableHead><TableHead>Size / dimensions</TableHead>
                  <TableHead>CMS usage / domains</TableHead><TableHead>Uploaded</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{visible.map((asset) => (
                  <TableRow key={asset.id} className={pendingDeleteIds.has(asset.id) ? 'opacity-50' : ''}>
                    <TableCell><input type="checkbox" aria-label={`Select ${asset.public_id}`} checked={selected.has(asset.id)}
                      disabled={disabled || pendingDeleteIds.has(asset.id) || asset.usageStatus !== 'unused'}
                      onChange={(event) => setSelected((previous) => {
                        const next = new Set(previous);
                        if (event.target.checked) next.add(asset.id); else next.delete(asset.id);
                        return next;
                      })} /></TableCell>
                    <TableCell><button type="button" aria-label={`Preview ${asset.public_id}`} onClick={() => setDetailsId(asset.id)}><MediaThumbnail asset={asset} /></button></TableCell>
                    <TableCell className="max-w-64">
                      <button type="button" className="block w-full truncate text-left text-sm font-medium hover:underline" onClick={() => setDetailsId(asset.id)} title={asset.public_id}>{asset.public_id}</button>
                      <span className="text-xs text-muted-foreground">{asset.resource_type} / {asset.format} / {asset.account}</span>
                    </TableCell>
                    <TableCell className="text-xs">{formatMediaSize(asset.bytes)}<br />{asset.width} x {asset.height}
                      {asset.duration !== null && <><br />{asset.duration.toFixed(1)} sec</>}
                    </TableCell>
                    <TableCell className="max-w-60 text-xs">
                      <span className={asset.usageStatus === 'inUse' ? 'text-success' : asset.usageStatus === 'unknown' ? 'text-danger' : 'text-muted-foreground'}>{usageLabel(asset.usageStatus)}</span>
                      <p className="truncate" title={asset.domains.join(', ')}>{asset.domains.join(', ') || (asset.shared ? 'Shared/global' : '-')}</p>
                      <Button size="sm" variant="link" onClick={() => setDetailsId(asset.id)}>Usage details</Button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{dateLabel(asset.created_at)}</TableCell>
                    <TableCell><div className="flex items-center gap-1">
                      <Button size="icon-sm" variant="ghost" title="Details" onClick={() => setDetailsId(asset.id)}><Info /></Button>
                      <a href={asset.secure_url} target="_blank" rel="noopener noreferrer" title="Open original" className="p-2"><ExternalLink className="h-4 w-4" /></a>
                      <Button size="icon-sm" variant="ghost" title="Copy URL" onClick={() => void copyUrl(asset.secure_url)}><Copy /></Button>
                      <Button size="icon-sm" variant="ghost" title={asset.usageStatus === 'unused' ? 'Delete asset' : 'Deletion blocked: usage is referenced or unknown'}
                        disabled={disabled || deleting || asset.usageStatus !== 'unused'} onClick={() => beginDelete([asset])}
                        loading={pendingDeleteIds.has(asset.id)}><Trash2 className="text-danger" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
          <Pagination {...paginationProps} itemLabel="assets" />
        </>
      )}

      <Dialog open={uploadOpen} onOpenChange={(open) => { if (!uploading) { setUploadOpen(open); if (!open) setFile(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload media</DialogTitle>
            <DialogDescription>JPEG, PNG, GIF, WebP, AVIF, MP4, WebM or MOV. Up to 50 MB per file.</DialogDescription>
          </DialogHeader>
          <div className="rounded border-2 border-dashed border-border p-5 text-center"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (uploading) return;
              if (event.dataTransfer.files.length !== 1) { toast.error('Drop one file at a time.'); return; }
              chooseFile(event.dataTransfer.files[0]);
            }}>
            <label className="block cursor-pointer text-sm">Drop a file here or choose a file
              <input className="mt-3 block w-full text-xs" type="file" accept={MEDIA_ACCEPT.join(',')} disabled={uploading}
                onChange={(event) => { chooseFile(event.target.files?.[0]); event.target.value = ''; }} />
            </label>
          </div>
          {file && <p className="break-all text-sm">{file.name} ({formatMediaSize(file.size)})</p>}
          {filePreview && (file?.type.startsWith('video/')
            ? <video src={filePreview} controls preload="metadata" className="max-h-56 w-full rounded" />
            : <img src={filePreview} alt="Selected upload" className="max-h-56 w-full rounded object-contain" />)}
          {uploading && <div aria-live="polite" className="space-y-1 text-xs">
            <progress value={progress} max={100} className="w-full" />
            <p>{progress < 100 ? `Uploading ${progress}%` : 'Upload sent. Processing in storage...'}</p>
          </div>}
          <Button disabled={!file} loading={uploading} loadingText="Uploading..." onClick={() => void handleUpload()}>Upload</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!details} onOpenChange={(open) => { if (!open) setDetailsId(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>Media details</DialogTitle><DialogDescription className="break-all">{details?.public_id}</DialogDescription></DialogHeader>
          {details && <>
            {details.resource_type === 'video'
              ? <video key={details.id} src={details.secure_url} controls preload="metadata" className="max-h-72 w-full rounded" />
              : <img src={details.secure_url} alt={details.public_id} className="max-h-72 w-full rounded object-contain" />}
            <p className="text-sm">{details.resource_type} / {details.format} / {formatMediaSize(details.bytes)} / {details.width} x {details.height}
              {details.duration !== null ? ` / ${details.duration.toFixed(1)} seconds` : ''}</p>
            <p className="text-xs">Account: {details.account} | Folder: {details.folder || '(root)'} | Uploaded: {dateLabel(details.created_at)}</p>
            <a className="break-all text-xs text-primary underline" href={details.secure_url} target="_blank" rel="noopener noreferrer">{details.secure_url}</a>
            <h3 className="text-sm font-semibold">{usageLabel(details.usageStatus)} ({details.usageCount ?? 'unverified'})</h3>
            {details.references.map((reference, index) => <div key={`${reference.documentId}-${reference.field}-${index}`} className="rounded border border-border p-2 text-xs">
              <p>{reference.model}: {reference.field}</p><p className="break-all">Document: {reference.documentId}</p>
              <p>{reference.shared ? 'Shared/global' : 'Domain'}: {reference.domains.join(', ') || 'No direct domain association'}</p>
            </div>)}
            <p className="text-xs text-muted-foreground">Usage includes inactive saved content. External embeds and files not referenced in the CMS cannot be verified.</p>
          </>}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={deleteTargets.length > 0} onOpenChange={(open) => { if (!open) setDeleteTargets([]); }}
        title={`Permanently delete ${deleteTargets.length} asset(s)?`}
        description={`This removes ${formatMediaSize(deleteTargets.reduce((sum, asset) => sum + asset.bytes, 0))} of original media. There is no undo. CMS references are freshly checked across all domains for this batch.`}
        body={<div className="space-y-3">
          <ul className="max-h-32 overflow-y-auto text-xs">{deleteTargets.map((asset) => <li key={asset.id} className="break-all">{asset.public_id} ({asset.resource_type}, {asset.account})</li>)}</ul>
          <p>A reference check and storage deletion are not atomic. Do not delete while another editor is attaching these files. Newly uploaded files may still be awaiting attachment.</p>
          <label className="flex items-start gap-2"><input type="checkbox" checked={acknowledged} disabled={deleting} onChange={(event) => setAcknowledged(event.target.checked)} />
            I have verified these assets are not used externally or currently being attached by another editor.
          </label>
        </div>}
        confirmLabel="Permanently delete" disabled={!acknowledged || deleting} onConfirm={() => { void handleDelete(); }} />
    </div>
  );
}
